// Ported from the desktop app (ui/src/lib/recording.ts).
// Pure helpers to turn engine `Recorded` events into pattern notes.
import type { NoteEvent, Pattern } from "@eb/shared";
import { recordedToTick } from "@eb/shared";

export interface QuantizeSettings {
  enabled: boolean;
  gridTicks: number;
  strength: number;
}

/** Insert (or merge) a recorded hit. Returns the new events array and the inserted note's tick. */
export function insertRecorded(
  events: NoteEvent[],
  pad: number,
  velocity: number,
  audibleTick: number,
  swing: number,
  patternLength: number,
  q: QuantizeSettings,
  defaultLength: number,
  noteNumber = 60,
): { events: NoteEvent[]; tick: number } {
  const tick = recordedToTick(audibleTick, swing, q.gridTicks, q.enabled ? q.strength : 0, patternLength);
  const existing = events.findIndex((e) => e.pad === pad && e.tick === tick && e.note === noteNumber);
  const note: NoteEvent = { tick, pad, note: noteNumber, velocity, length_ticks: defaultLength };
  const next = events.slice();
  if (existing >= 0) next[existing] = { ...next[existing], velocity: Math.max(next[existing].velocity, velocity) };
  else next.push(note);
  next.sort((a, b) => a.tick - b.tick || a.pad - b.pad);
  return { events: next, tick };
}

/** Length of a held note from its start/release audible ticks (wrapping at the loop). */
export function heldLength(
  startAudible: number,
  releaseAudible: number,
  patternLength: number,
  minLength: number,
): number {
  let d = releaseAudible - startAudible;
  if (d < 0) d += patternLength;
  return Math.max(minLength, Math.round(d));
}

export function patternLength(p: Pattern): number {
  return Math.max(1, Math.min(8, p.length_bars)) * 3840;
}
